import { RATE_CARD_FIELDS, ISSUING_BANK_NAMES } from "../constants/enums"
import { DisplaySectionFieldType } from "../types/components";
import { Country, Provider, Quote, RateQuery } from "../types/moneyTransfer";
import * as _ from "lodash";
import ENV from "../constants/environment";
import { ROUTES as API_ROUTES } from "../constants/api";
import { ICON_TOKEN_REGEX } from "../constants/enums";

/**
   * Returns value from str
   * @private
   * @returns {str} 56.00 instead of 1 USD = 56.00 PHP
   */
const formatExchangeRate = (str: string) => {
  const startIndex = str.indexOf("= ");
  const endIndex = str.indexOf(" ", startIndex + 2);
  const exchange_rate = str.slice(startIndex + 2, endIndex);
  return exchange_rate;
}

/**
   * Returns value from str, removing the currency
   * @private
   * @returns {str} 100.0 instead of 100.0 USD
   */
const removeCurrency = (str: string) => {
  return str.slice(0, str.indexOf(" "));
}

export const createGAParamsCashPickupReview = (provider: Provider, quote: Quote, availableCountries: Country[], rateQuery: RateQuery, issuingBankId: number) => {
  const serviceProvider =
    provider.name === "Brightwell" ? "Transfast" : provider.name;
  const destinationCountry = availableCountries.find((country) => country.iso === rateQuery.destinationCountry);

  return {
    remittance_service_provider: serviceProvider,
    destination_country: destinationCountry?.name,
    destination_currency: rateQuery.receiveCurrency,
    send_amount: rateQuery.sendAmount,
    exchange_rate: formatExchangeRate(
      _.find(quote.fields, (f: DisplaySectionFieldType) => f.id == RATE_CARD_FIELDS.FX_RATE)
        ?.formattedValue || ""
    ),
    receive_amount: removeCurrency(
      _.find(
        quote.fields,
        (f: DisplaySectionFieldType) => f.id == RATE_CARD_FIELDS.TOTAL_RECEIVE_AMOUNT
      )?.formattedValue || ""
    ),
    transfer_fee: removeCurrency(
      _.find(quote.fields, (f: DisplaySectionFieldType) => f.id == RATE_CARD_FIELDS.FEE)?.formattedValue || ""
    ),
    total_cost: removeCurrency(
      _.find(quote.fields, (f: DisplaySectionFieldType) => f.id == RATE_CARD_FIELDS.TOTAL_COST)
        ?.formattedValue || ""
    ),
    issuance_bank: ISSUING_BANK_NAMES[issuingBankId as keyof typeof ISSUING_BANK_NAMES],
  };
}

const DEBUG_ENVIRONMENTS = ["dev", "qa1", "qa2", "readiness"];

const isDebugEnvironment = () =>
  DEBUG_ENVIRONMENTS.includes(String(ENV.BW_ENV).toLowerCase());

// allow overriding numeric values via URL parameters for debugging purposes on session end modal
// e.g. a URL like http://localhost:3000/enrollment?sessionTimeout=30&sessionWarning=10
export const getNumericOverride = (key: string): number | null => {
  if (!isDebugEnvironment()) return null;

  const param = new URLSearchParams(window.location.search).get(key);
  if (!param) return null;

  const parsed = Number(param);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Resolves an {icon:…} token in a display string. The token sits either before or
 * after the text — `isIconFirst` says which, so the icon renders where the string puts it:
 * "{icon:assets/operators/gcash.png} GCash" → isIconFirst true
 * "GCash (*5550) {icon:assets/operators/gcash.png}" → isIconFirst false
 */
export const parseIconToken = (display: string) => {
  const match = display.match(ICON_TOKEN_REGEX);
  return {
    iconUrl: match ? API_ROUTES.SEND_MONEY.ICON(match[1]) : undefined,
    text: display.replace(ICON_TOKEN_REGEX, ""),
    isIconFirst: match?.index === 0,
  };
};